import React from "react";
import { Navbar } from "../../../Components/Navbar/Navbar";
import { Footer } from "../../../Components/Footer/footer";
import "./admin-smart-orders.css";

import {
  collection,
  query,
  orderBy,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";

type Order = {
  id: string;
  product: "inel" | "bratara" | "breloc" | "card" | string;
  quantity: number;
  fullName: string;
  email: string;
  phone: string;
  organization: string;
  address?: string;
  notes?: string;
  consent?: boolean;
  status: string;
  createdAt?: Timestamp;
};

const STATUSES = ["new", "in_review", "confirmed", "shipped", "done", "canceled"] as const;

export default function AdminSmartOrdersPage() {
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<"" | Order["status"]>("");
  const [orgFilter, setOrgFilter] = React.useState<string>("");         // NEW
  const [search, setSearch] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const col = collection(db, "smart_orders");
      const qBase = query(col, orderBy("createdAt", "desc"));
      const snap = await getDocs(qBase);
      let rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Order[];

      if (statusFilter) rows = rows.filter((r) => r.status === statusFilter);
      setOrders(rows);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Build org list from loaded data
  const orgOptions = React.useMemo(() => {
    const set = new Set<string>();
    orders.forEach((o) => o.organization && set.add(o.organization));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const applySearchAndFilters = (rows: Order[]) => {
    let out = rows;
    if (orgFilter) out = out.filter((r) => r.organization === orgFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter((r) =>
        [r.fullName, r.email, r.phone, r.organization, r.product, r.notes ?? "", r.address ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(s)
      );
    }
    return out;
  };

  const rows = applySearchAndFilters(orders);

  const onChangeStatus = async (id: string, next: string) => {
    await updateDoc(doc(db, "smart_orders", id), { status: next });
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status: next } : o)));
  };

  const onDelete = async (id: string) => {
    if (!window.confirm("Ștergi această comandă? Acțiunea este definitivă.")) return;
    await deleteDoc(doc(db, "smart_orders", id));
    setOrders((prev) => prev.filter((o) => o.id !== id));
  };

  // -------- CSV Export (respects current filters) ----------
  function csvEscape(v: unknown) {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }
  function exportCSV() {
    const data = rows; // already filtered by status/search/org
    const header = [
      "createdAt",
      "fullName",
      "email",
      "phone",
      "organization",
      "address",
      "notes",
      "product",
      "quantity",
      "status",
    ];
    const lines = [header.join(",")];

    data.forEach((o) => {
      const created =
        o.createdAt?.toDate ? o.createdAt.toDate().toISOString() : "";
      lines.push(
        [
          created,
          o.fullName,
          o.email,
          o.phone,
          o.organization,
          o.address ?? "",
          o.notes ?? "",
          o.product,
          o.quantity,
          o.status,
        ]
          .map(csvEscape)
          .join(",")
      );
    });

    const csv = lines.join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }); // BOM for Excel
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const orgSuffix = orgFilter ? `_${orgFilter.replace(/\W+/g, "_")}` : "";
    a.href = url;
    a.download = `smart_orders${orgSuffix || "_all"}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="page-container">
      <Navbar />
      <div className="page-content admin-orders">
        <h1>Admin – Comenzi Smart SANITAS</h1>

        <div className="toolbar">
          <div className="filters">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
              <option value="">Toate statusurile</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            {/* NEW: Organization filter */}
            <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
              <option value="">Toate organizațiile</option>
              {orgOptions.map((org) => (
                <option key={org} value={org}>
                  {org}
                </option>
              ))}
            </select>

            <input
              placeholder="Caută: nume, email, telefon, organizație, produs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="actions">
            {/* NEW: Export CSV */}
            <button
              className="btn btn-primary"
              onClick={exportCSV}
              disabled={rows.length === 0}
              title="Exportă CSV (respectă filtrele curente)"
            >
              Export CSV
            </button>

            <button className="btn btn-ghost" onClick={load} disabled={loading}>
              {loading ? "Se încarcă…" : "Reîncarcă"}
            </button>
          </div>
        </div>

        <div className="orders-table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Membru</th>
                <th>Contact</th>
                <th>Produs</th>
                <th>Org / Adresă</th>
                <th>Note</th>
                <th>Status</th>
                <th style={{ width: 110 }}>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", color: "#666" }}>
                    {loading ? "Se încarcă…" : "Nicio comandă găsită."}
                  </td>
                </tr>
              ) : (
                rows.map((o) => (
                  <tr key={o.id}>
                    <td>
                      {o.createdAt?.toDate ? o.createdAt.toDate().toLocaleString() : "—"}
                    </td>
                    <td>
                      <div className="strong">{o.fullName || "—"}</div>
                      <div className="muted">
                        {o.quantity} × {o.product}
                      </div>
                    </td>
                    <td>
                      <div>{o.email}</div>
                      <div className="muted">{o.phone}</div>
                    </td>
                    <td>
                      <span className="pill">{o.product}</span>
                    </td>
                    <td>
                      <div>{o.organization}</div>
                      {o.address ? <div className="muted">{o.address}</div> : null}
                    </td>
                    <td className="notes">{o.notes || "—"}</td>
                    <td>
                      <select
                        value={o.status}
                        onChange={(e) => onChangeStatus(o.id, e.target.value)}
                        className={`status ${o.status}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="row-actions">
                      <button className="btn btn-ghost small" onClick={() => onDelete(o.id)}>
                        Șterge
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}